import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useLocation, useRouter } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { Button, EmptyState, Skeleton } from '@/components';
import { PlannerPage, type PlannerDay } from '@/components/personal/PlannerPage';
import { announce } from '@/lib/announce';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  effectiveTimezone,
  weekStartForLocale,
  type WeekStart,
} from '@/lib/locale-formatting';
import { useLocalePreferencesValue } from '@/lib/locale-preferences';
import {
  calendarAccountsQueryOptions,
  plannerQueryOptions,
  useCreatePlannerFocusBlock,
  useDeletePlannerFocusBlock,
  useLinkPlannerFocusBlockCard,
  usePatchPlannerFocusBlockTime,
  usePlannerBoardFilters,
  usePlannerCalendarAuthorize,
  usePlannerCalendarCallback,
  usePlannerCalendarDisconnect,
  usePlannerCalendarDisplayed,
  usePlannerCompleteCard,
  usePlannerIncludeUnassigned,
  usePlannerSuggestions,
  useUnlinkPlannerFocusBlockLink,
  usePlannerWorkingDays,
  usePlannerWorkingHours,
} from '@/lib/planner';
import { signInHref, useSession } from '@/lib/session';
import type {
  CalendarProvider,
  PlannerSuggestion,
  PlannerSuggestions,
} from '@molt/shared';

type InterfaceTranslate = ReturnType<typeof useInterfaceLanguage>['t'];

export const Route = createFileRoute('/planner')({
  staticData: { layout: 'full-bleed' },
  component: PlannerRoute,
});

/**
 * `FR-NOTIF-032` AC4 -- "Given a user overrides their timezone, when they open
 * Planner, then the grid is drawn in the overridden timezone."
 *
 * ---------------------------------------------------------------------------
 * THE CANONICAL UNIT ON THIS ROUTE IS A CALENDAR DATE, NOT AN INSTANT
 * ---------------------------------------------------------------------------
 * Every column the Planner draws is a *day in the member's zone*, so the value
 * this file passes around is a `YYYY-MM-DD` key resolved through
 * `effectiveTimezone`, and instants are derived from it at the two edges that
 * need one (the wire, and `Intl`). The route previously anchored on
 * `startOfUtcDay` and formatted labels with no `timeZone` option at all, which
 * put the anchor in UTC and the label in the DEVICE zone -- so a member with an
 * override could be shown a column headed "today" that was not their today, and
 * `effectiveTimezone` was never consulted on this path.
 *
 * Stepping is `Date.UTC(y, m - 1, d + n)` on the key rather than
 * `+ n * 86_400_000` on an instant. A fixed millisecond stride is wrong across
 * a daylight-saving transition -- it lands on 23:00 of the previous day or
 * 01:00 of the next -- and every previous/next/today control on this page steps
 * by whole days.
 *
 * ⚠ `PlannerDay.startAt` is deliberately NOT the true instant at which the day
 * begins in that zone. `PlannerPage.tsx` places every focus block and external
 * event with `sameDay(block.startAt, day.startAt)`, which compares
 * `slice(0, 10)` -- UTC date strings -- and reads hours with
 * `new Date(x).getHours()`. Handing it a column whose UTC date is the previous
 * day would silently move every block one column over. So the column keeps the
 * invariant `startAt === `${key}T00:00:00.000Z``; what the zone decides is
 * WHICH date the key is. Drawing the contents of a column in the member's zone
 * needs `PlannerPage.tsx`, which this chunk does not own -- see `BE-44.8`.
 */
const DATE_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function PlannerRoute() {
  const location = useLocation();
  const router = useRouter();
  const { t } = useInterfaceLanguage();
  const session = useSession();
  const signedIn = !session.isSignedOut && !session.isPending && session.profile !== null;
  /*
   * The two stored values AC4 turns on, read from the context `__root.tsx`
   * provides -- so a signed-out or still-loading render falls back to
   * `DEFAULT_LOCALE_PREFERENCES` (`language: 'en'`, no override) rather than
   * suspending the grid.
   *
   * `language` governs the WEEK START, the TIMEZONE, and -- since `BE-44.9` --
   * the label's date FORMAT. The labels were on
   * `useInterfaceLanguage().locale`, the B80 catalogue's `resolvedLocale`
   * (`en-GB` for every member on every request, so the grid read day-first
   * for everyone); `FR-NOTIF-032` ("a per-user language setting drives date,
   * time and calendar formatting") and `FR-EXP-029` ("...which is also the
   * source of date-format preference") say it must be `preferences.language`
   * instead, matching every other date surface in the product
   * (`formatLocaleCardDate`, `formatLocaleDateTimeLabel`). The swap re-pinned
   * two EXACT assertions in `e2e/FR-NOTIF-022.spec.ts` (`Tuesday 25 Aug` ->
   * `Tuesday, Aug 25` for the default `en`). `t(...)` catalogue copy still
   * comes from `useInterfaceLanguage()`.
   */
  const preferences = useLocalePreferencesValue();
  const timeZone = effectiveTimezone(preferences);
  const weekStart = weekStartForLocale(preferences.language);
  const [responsiveDays, setResponsiveDays] = useState<1 | 3 | 7>(() =>
    responsivePlannerDays(),
  );
  useEffect(() => {
    function handleResize(): void {
      setResponsiveDays(responsivePlannerDays());
    }
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const routeQuery = plannerRouteQuery(location.searchStr, {
    timeZone,
    weekStart,
    maxVisibleDays: responsiveDays,
  });
  const visibleDays = useMemo(
    () =>
      plannerVisibleDays(
        routeQuery.fromKey,
        routeQuery.visibleDays,
        preferences.language,
      ),
    [preferences.language, routeQuery.fromKey, routeQuery.visibleDays],
  );
  const plannerQuery = { from: routeQuery.from, to: routeQuery.to };
  const planner = useQuery({ ...plannerQueryOptions(plannerQuery), enabled: signedIn });
  const calendarAccounts = useQuery({
    ...calendarAccountsQueryOptions(),
    enabled: signedIn,
  });
  const boardFilters = usePlannerBoardFilters();
  const includeUnassigned = usePlannerIncludeUnassigned();
  const workingHours = usePlannerWorkingHours();
  const workingDays = usePlannerWorkingDays();
  const authorizeCalendar = usePlannerCalendarAuthorize();
  const callbackCalendar = usePlannerCalendarCallback();
  const disconnectCalendar = usePlannerCalendarDisconnect();
  const calendarDisplayed = usePlannerCalendarDisplayed();
  const createFocusBlock = useCreatePlannerFocusBlock();
  const patchFocusBlock = usePatchPlannerFocusBlockTime();
  const linkFocusBlock = useLinkPlannerFocusBlockCard();
  // Link-scoped, not card-scoped: an orphaned link has no card id to name.
  const unlinkFocusBlock = useUnlinkPlannerFocusBlockLink();
  const deleteFocusBlock = useDeletePlannerFocusBlock();
  const completeCard = usePlannerCompleteCard();
  const suggestions = usePlannerSuggestions();
  const [visibleSuggestions, setVisibleSuggestions] = useState<PlannerSuggestions>();
  const mutationError =
    boardFilters.error?.message ??
    includeUnassigned.error?.message ??
    workingHours.error?.message ??
    workingDays.error?.message ??
    authorizeCalendar.error?.message ??
    callbackCalendar.error?.message ??
    disconnectCalendar.error?.message ??
    calendarDisplayed.error?.message ??
    createFocusBlock.error?.message ??
    patchFocusBlock.error?.message ??
    linkFocusBlock.error?.message ??
    unlinkFocusBlock.error?.message ??
    deleteFocusBlock.error?.message ??
    errorMessage(completeCard.error) ??
    suggestions.error?.message ??
    null;

  /**
   * ⚠ Takes a DATE KEY, and writes one. Not an instant, and the difference is
   * load bearing rather than tidy.
   *
   * Round-tripping an instant through the zone is not idempotent at a negative
   * offset: `zoneDateKey('2026-09-15T00:00:00.000Z', 'Pacific/Honolulu')` is
   * `2026-09-14`, so a "next week" button that wrote `…T00:00:00.000Z` would
   * step forward seven days and be read back as six, forever, for every member
   * west of Greenwich. A key is the same date in every zone.
   *
   * `requestedDays` rather than the clamped `visibleDays`: `responsivePlannerDays`
   * narrows a 7-day view to 3 or 1 on a small viewport, and writing the clamped
   * value into the URL would make that narrowing PERMANENT the first time a
   * member pressed Next on a phone.
   */
  function setRange(fromKey: string, visibleDayCount = routeQuery.requestedDays): void {
    const search = new URLSearchParams(location.searchStr);
    search.set('from', fromKey);
    search.set('days', String(visibleDayCount));
    const query = search.toString();
    router.history.replace(`${location.pathname}${query === '' ? '' : `?${query}`}`);
    router.history.flush();
  }

  function startConnection(provider: CalendarProvider): void {
    authorizeCalendar.mutate(
      { provider, body: { externalAccountRef: `${provider}-${Date.now()}` } },
      {
        onSuccess: (response) => {
          callbackCalendar.mutate(
            { provider: response.provider, state: response.state, code: 'browser' },
            {
              onSuccess: () =>
                announce(
                  t('planner.announce.calendarConnected', {
                    provider: providerLabel(provider, t),
                  }),
                ),
            },
          );
        },
      },
    );
  }

  if (session.isSignedOut) {
    return (
      <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <EmptyState
          tone="neutral"
          size="panel"
          title={t('planner.signInTitle')}
          body={t('planner.signInBody')}
          action={
            <Button
              as="a"
              variant="primary"
              href={signInHref({ pathname: '/planner', search: location.searchStr })}
            >
              {t('planner.signInAction')}
            </Button>
          }
        />
      </main>
    );
  }

  if (session.isPending) {
    return (
      <main className="mx-auto grid w-full max-w-5xl gap-3 px-4 py-6 sm:px-6">
        <Skeleton shape="block" />
        <Skeleton shape="row" />
        <Skeleton shape="row" />
      </main>
    );
  }

  if (session.profile === null) {
    return (
      <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <EmptyState
          tone="error"
          size="panel"
          title={t('planner.loadFailedTitle')}
          body={t('planner.genericError')}
        />
      </main>
    );
  }

  const plannerData =
    planner.data === undefined
      ? undefined
      : {
          ...planner.data,
          connectedCalendarAccounts:
            calendarAccounts.data?.accounts ?? planner.data.connectedCalendarAccounts,
          connectedCalendars:
            calendarAccounts.data?.calendars ?? planner.data.connectedCalendars,
        };

  return (
    <PlannerPage
      planner={plannerData}
      suggestions={visibleSuggestions}
      visibleDays={visibleDays}
      loading={planner.isPending || calendarAccounts.isPending}
      error={
        planner.error != null || calendarAccounts.error != null
          ? t('planner.genericError')
          : null
      }
      busyAction={busyAction({
        boardFilters: boardFilters.isPending,
        includeUnassigned: includeUnassigned.isPending,
        workingHours: workingHours.isPending,
        workingDays: workingDays.isPending,
        authorizeCalendar: authorizeCalendar.isPending || callbackCalendar.isPending,
        disconnectCalendar: disconnectCalendar.isPending,
        calendarDisplayed: calendarDisplayed.isPending,
        createFocusBlock: createFocusBlock.variables?.cardIds?.[0],
        patchFocusBlock: patchFocusBlock.variables?.focusBlockId,
        linkFocusBlock: linkFocusBlock.variables?.focusBlockId,
        unlinkFocusBlock: unlinkFocusBlock.variables?.focusBlockId,
        deleteFocusBlock: deleteFocusBlock.variables,
        completeCard: completeCard.variables?.cardId,
        suggestions: suggestions.isPending,
      })}
      mutationError={mutationError}
      onPreviousRange={() =>
        setRange(addDateKeyDays(routeQuery.fromKey, -routeQuery.visibleDays))
      }
      onNextRange={() =>
        setRange(addDateKeyDays(routeQuery.fromKey, routeQuery.visibleDays))
      }
      onToday={() => setRange(zoneDateKey(new Date().toISOString(), timeZone))}
      onRetry={() => {
        void planner.refetch();
        void calendarAccounts.refetch();
      }}
      onBoardFiltersChange={(boardIds) => {
        boardFilters.mutate(
          { boardIds: [...boardIds] },
          { onSuccess: () => announce(t('planner.announce.boardFiltersSaved')) },
        );
      }}
      onIncludeUnassignedChange={(next) => {
        includeUnassigned.mutate(next, {
          onSuccess: () => announce(t('planner.announce.cardFilterSaved')),
        });
      }}
      onWorkingHoursChange={(next) => {
        workingHours.mutate(next, {
          onSuccess: () => announce(t('planner.announce.workingHoursSaved')),
        });
      }}
      onWorkingDaysChange={(next) => {
        workingDays.mutate([...next], {
          onSuccess: () => announce(t('planner.announce.workingDaysSaved')),
        });
      }}
      onStartCalendarConnection={startConnection}
      onToggleCalendarDisplayed={(calendarId, displayed) => {
        calendarDisplayed.mutate(
          { calendarId, displayed },
          { onSuccess: () => announce(t('planner.announce.calendarDisplaySaved')) },
        );
      }}
      onDisconnectCalendarAccount={(accountId) => {
        disconnectCalendar.mutate(accountId, {
          onSuccess: () => announce(t('planner.announce.calendarDisconnected')),
        });
      }}
      onCreateFocusBlock={(cardId, startAt, endAt) => {
        createFocusBlock.mutate(
          { cardIds: [cardId], startAt, endAt },
          { onSuccess: () => announce(t('planner.announce.focusScheduled')) },
        );
      }}
      onMoveFocusBlock={(focusBlockId, startAt, endAt) => {
        patchFocusBlock.mutate(
          { focusBlockId, startAt, endAt },
          { onSuccess: () => announce(t('planner.announce.focusMoved')) },
        );
      }}
      onResizeFocusBlock={(focusBlockId, startAt, endAt) => {
        patchFocusBlock.mutate(
          { focusBlockId, startAt, endAt },
          { onSuccess: () => announce(t('planner.announce.focusResized')) },
        );
      }}
      onLinkCardToFocusBlock={(focusBlockId, cardId) => {
        linkFocusBlock.mutate(
          { focusBlockId, cardId },
          { onSuccess: () => announce(t('planner.announce.cardLinked')) },
        );
      }}
      onRemoveLinkedCard={(focusBlockId, linkId) => {
        unlinkFocusBlock.mutate(
          { focusBlockId, linkId },
          {
            onSuccess: () => announce(t('planner.announce.cardRemoved')),
          },
        );
      }}
      onDeleteFocusBlock={(focusBlockId) => {
        deleteFocusBlock.mutate(focusBlockId, {
          onSuccess: () => announce(t('planner.announce.focusDeleted')),
        });
      }}
      onCompleteCard={(cardId, boardId, completed) => {
        completeCard.mutate(
          { cardId, boardId, completed },
          {
            onSuccess: () =>
              announce(
                completed
                  ? t('planner.announce.cardCompleted')
                  : t('planner.announce.cardReopened'),
              ),
          },
        );
      }}
      /*
       * ---------------------------------------------------------------------
       * `FR-NOTIF-025` -- "constrain proposals by source board"
       * ---------------------------------------------------------------------
       * The parameter is the PROPOSAL-scoped board constraint the Smart
       * schedule panel collects, and it is deliberately NOT
       * `plannerData.selectedBoardFilters`. That stored selection is
       * `FR-NOTIF-020`'s clause -- it filters *the panel* -- and the server
       * already applies it to every suggestion query, so echoing it back on the
       * wire would be a no-op wearing a feature's clothes. Two clauses, two
       * scopes, and this handler carries the second one.
       *
       * `undefined` means "no proposal-scoped constraint": the field never
       * reaches the JSON and the request is byte-for-byte the one this route
       * has always sent.
       *
       * The `?` is a compatibility affordance, not a claim about the wire.
       * `PlannerPageProps` declares this callback as
       * `(sourceBoardIds: readonly string[] | undefined) => void` and always
       * passes an argument, so the optional marker buys nothing at runtime --
       * it is here so the handler compiles against that shape AND against the
       * `() => void` the prop carried before the Smart-schedule control landed,
       * which is what let this file and the panel be written side by side.
       */
      onRequestSuggestions={(sourceBoardIds?: readonly string[]) => {
        const plannerRules =
          plannerData === undefined
            ? {}
            : {
                workingHoursStart: plannerData.preferences.workingHours.start,
                workingHoursEnd: plannerData.preferences.workingHours.end,
                workingDays: plannerWorkingDayMask(plannerData.preferences.workingDays),
                externalEvents: plannerData.externalEvents
                  .filter((event) => event.busy)
                  .map((event) => ({
                    startAt: event.startAt,
                    endAt: event.endAt,
                  })),
              };
        suggestions.mutate(
          {
            from: plannerQuery.from,
            to: plannerQuery.to,
            durationMinutes: 60,
            maxSuggestions: 5,
            ...plannerRules,
            /*
             * Forwarded exactly as given. An empty or duplicated list is NOT
             * smoothed into `undefined` here, and the omission is the point:
             * `undefined` means "every board the caller can read", which is the
             * OPPOSITE of what a user who deselected every board asked for, so
             * substituting it would answer a question nobody put. The list is
             * refused instead -- `plannerSuggestionsBodySchema.parse` in
             * `lib/planner.ts` rejects `[]`, a duplicate and a non-ULID before
             * the request is issued, and the failure lands in `mutationError`
             * beside every other planner failure. Loud beats plausible.
             */
            sourceBoardIds:
              sourceBoardIds === undefined ? undefined : [...sourceBoardIds],
          },
          {
            onSuccess: (result) => {
              setVisibleSuggestions(result);
              announce(t('planner.announce.suggestionsReady'));
            },
          },
        );
      }}
      onAcceptSuggestion={(suggestion) => {
        createFocusBlock.mutate(
          {
            cardIds: suggestion.cardIds,
            startAt: suggestion.startAt,
            endAt: suggestion.endAt,
          },
          {
            onSuccess: () => {
              setVisibleSuggestions((current) => removeSuggestion(current, suggestion));
              announce(t('planner.announce.suggestionAccepted'));
            },
          },
        );
      }}
      onRejectSuggestion={(proposalId) => {
        setVisibleSuggestions((current) =>
          current === undefined
            ? undefined
            : {
                ...current,
                suggestions: current.suggestions.filter(
                  (suggestion) => suggestion.proposalId !== proposalId,
                ),
              },
        );
        announce(t('planner.announce.suggestionRejected'));
      }}
    />
  );
}

/**
 * The window the Planner draws, resolved in the member's own zone.
 *
 * `?from=` is read in BOTH spellings on purpose. A bare `YYYY-MM-DD` is taken
 * as a date key verbatim -- that is what `setRange` writes, and re-reading it
 * through the zone would shift it a day west of Greenwich. Anything else is
 * treated as an instant and resolved through `effectiveTimezone`, which is what
 * keeps every URL this route has ever produced, and every hand-typed one,
 * working.
 *
 * ⚠ The week snap is gated on the EFFECTIVE day count, not the requested one.
 * `responsivePlannerDays` narrows a 7-day view to 3 or 1 by viewport, and a
 * 1-day view snapped to Monday would answer a question nobody put: a member who
 * asked for one day asked for TODAY. `FR-NOTIF-032`'s "first day of week" is a
 * property of a week, so it applies exactly when a week is on screen.
 *
 * `requestedDays` is returned beside the clamped `visibleDays` so `setRange` can
 * write the URL back without baking a viewport clamp into it permanently.
 */
function plannerRouteQuery(
  searchStr: string,
  options: {
    readonly timeZone: string;
    readonly weekStart: WeekStart;
    readonly maxVisibleDays: 1 | 3 | 7;
  },
): {
  fromKey: string;
  from: string;
  to: string;
  visibleDays: 1 | 3 | 7;
  requestedDays: 1 | 3 | 7;
} {
  const params = new URLSearchParams(searchStr);
  const requestedFrom = params.get('from');
  const requestedDays = parseVisibleDays(params.get('days'));
  const visibleDays = Math.min(requestedDays, options.maxVisibleDays) as 1 | 3 | 7;

  const anchorKey =
    requestedFrom !== null && isCalendarDateKey(requestedFrom)
      ? requestedFrom
      : zoneDateKey(
          requestedFrom === null || Number.isNaN(Date.parse(requestedFrom))
            ? new Date().toISOString()
            : new Date(requestedFrom).toISOString(),
          options.timeZone,
        );
  const fromKey =
    visibleDays === 7 ? startOfWeekDateKey(anchorKey, options.weekStart) : anchorKey;

  return {
    fromKey,
    from: dateKeyInstant(fromKey),
    to: dateKeyInstant(addDateKeyDays(fromKey, visibleDays)),
    visibleDays,
    requestedDays,
  };
}

/**
 * The columns. `timeZone: 'UTC'` on both formatters is not a fallback and is
 * not the device zone leaking back in -- the instant handed to them is already
 * the UTC midnight of a date resolved in the member's zone, so formatting it in
 * UTC is what makes the label say that date. Reading it in the device zone,
 * which is what this function did before it took a zone at all, is exactly the
 * `FR-NOTIF-032` AC4 defect.
 *
 * `locale` is the stored `preferences.language` (since `BE-44.9`), not the
 * catalogue's `resolvedLocale` -- so the grid formats month-first for the
 * default `en` member, matching every other date surface in the product.
 *
 * Both formatters are built once rather than per day: seven columns re-entered
 * the `Intl` constructor fourteen times a render for values that cannot differ
 * between columns.
 */
function plannerVisibleDays(
  fromKey: string,
  visibleDays: 1 | 3 | 7,
  locale: string,
): PlannerDay[] {
  const longLabel = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  const shortLabel = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });

  return Array.from({ length: visibleDays }, (_, index) => {
    const key = addDateKeyDays(fromKey, index);
    const startAt = dateKeyInstant(key);
    const date = new Date(startAt);
    return {
      key,
      startAt,
      label: longLabel.format(date),
      shortLabel: shortLabel.format(date),
    };
  });
}

function parseVisibleDays(value: string | null): 1 | 3 | 7 {
  if (value === '1') return 1;
  if (value === '3') return 3;
  return 7;
}

function responsivePlannerDays(): 1 | 3 | 7 {
  if (typeof window === 'undefined') return 7;
  if (window.innerWidth < 640) return 1;
  if (window.innerWidth < 1024) return 3;
  return 7;
}

/**
 * ⚠ `DATE_KEY_RE` on its own is NOT this predicate, and the difference is the
 * route rendering at all.
 *
 * The key spelling of `?from=` is read verbatim, which puts it AHEAD of the
 * `Date.parse` guard the instant spelling still carries -- so it is the one
 * branch of `plannerRouteQuery` with nothing behind it. The regex matches
 * `2026-13-45`, and that key reaches `startOfWeekDateKey`, whose `new Date(...)`
 * is an Invalid Date, whose `getUTCDay()` is `NaN`, whose
 * `new Date(NaN).toISOString()` throws a `RangeError`: not a mis-rendered
 * label, the whole Planner failing to render. On a 1-day view it does not
 * throw, which is worse -- `2026-02-30` goes onto the wire as
 * `2026-02-30T00:00:00.000Z` while `plannerVisibleDays` normalises the same key
 * through `Date.UTC` and draws `2026-03-02`, a range disagreeing with the day
 * drawn against it with nothing red anywhere.
 *
 * The test is a ROUND TRIP rather than a stricter pattern, because `Date.UTC`
 * normalises out-of-range components: `2026-02-30` satisfies any regex worth
 * writing and still is not a date. A key that survives `addDateKeyDays(key, 0)`
 * unchanged is one. A rejected key falls through to the instant branch, which
 * fails its own `Date.parse` guard and lands on today -- the same fallback the
 * route gave a malformed `?from=` before this file took a timezone.
 */
function isCalendarDateKey(value: string): boolean {
  return DATE_KEY_RE.test(value) && addDateKeyDays(value, 0) === value;
}

/** The calendar date an instant falls on, in `timeZone`. `YYYY-MM-DD`. */
function zoneDateKey(instantIso: string, timeZone: string): string {
  const parts = zoneDateFormatter(timeZone).formatToParts(new Date(instantIso));
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((candidate) => candidate.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/**
 * ⚠ Falls back to UTC rather than throwing. `effectiveTimezone` returns a
 * stored `timezoneOverride` WITHOUT re-validating it -- the server validates on
 * write -- and `Intl.DateTimeFormat` throws a `RangeError` on an identifier it
 * does not know. A throw here is not a mis-rendered label; it is the whole
 * Planner route failing to render, because this runs during `plannerRouteQuery`
 * rather than inside a leaf. A member whose override became unreadable sees the
 * grid in UTC, which is the same thing an unconfigured member with no device
 * zone already sees.
 */
function zoneDateFormatter(timeZone: string): Intl.DateTimeFormat {
  const options = { year: 'numeric', month: '2-digit', day: '2-digit' } as const;
  try {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone });
  } catch {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone: 'UTC' });
  }
}

/**
 * A date key as the instant the panel and the wire both expect: UTC midnight.
 * See the module docblock for why this is NOT the instant at which the day
 * begins in the member's zone.
 */
function dateKeyInstant(dateKey: string): string {
  return `${dateKey}T00:00:00.000Z`;
}

/**
 * Whole-day stepping, done as calendar arithmetic rather than as a fixed
 * millisecond stride. `Date.UTC` normalises an out-of-range day, so month and
 * year ends need no special case, and a daylight-saving transition cannot land
 * the result on 23:00 of the day before.
 */
function addDateKeyDays(dateKey: string, days: number): string {
  const match = DATE_KEY_RE.exec(dateKey);
  if (match === null) return dateKey;
  return new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + days),
  )
    .toISOString()
    .slice(0, 10);
}

/**
 * `FR-NOTIF-032` -- "a per-user language setting drives ... first day of week".
 * The week start is DERIVED by `weekStartForLocale`, never stored: the same
 * requirement's edge case rules out a second preference beside the language,
 * and the live `user_preferences` catalogue carries no `week_start` column to
 * read even if one were wanted.
 */
function startOfWeekDateKey(dateKey: string, weekStart: WeekStart): string {
  const dayOfWeek = new Date(dateKeyInstant(dateKey)).getUTCDay();
  return addDateKeyDays(dateKey, -((dayOfWeek - weekStart + 7) % 7));
}

function removeSuggestion(
  current: PlannerSuggestions | undefined,
  suggestion: PlannerSuggestion,
): PlannerSuggestions | undefined {
  if (current === undefined) return undefined;
  return {
    ...current,
    suggestions: current.suggestions.filter(
      (candidate) => candidate.proposalId !== suggestion.proposalId,
    ),
  };
}

function providerLabel(provider: CalendarProvider, t: InterfaceTranslate): string {
  return t(
    provider === 'google'
      ? 'planner.calendar.provider.google'
      : 'planner.calendar.provider.outlook',
  );
}

function plannerWorkingDayMask(days: readonly string[]): number {
  const bits: Record<string, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };
  return days.reduce((mask, day) => mask | (1 << (bits[day] ?? 0)), 0);
}

function errorMessage(error: unknown): string | null {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return null;
}

function busyAction(actions: {
  readonly boardFilters: boolean;
  readonly includeUnassigned: boolean;
  readonly workingHours: boolean;
  readonly workingDays: boolean;
  readonly authorizeCalendar: boolean;
  readonly disconnectCalendar: boolean;
  readonly calendarDisplayed: boolean;
  readonly createFocusBlock: string | undefined;
  readonly patchFocusBlock: string | undefined;
  readonly linkFocusBlock: string | undefined;
  readonly unlinkFocusBlock: string | undefined;
  readonly deleteFocusBlock: string | undefined;
  readonly completeCard: string | undefined;
  readonly suggestions: boolean;
}): string | null {
  if (actions.suggestions) return 'suggestions';
  return (
    actions.createFocusBlock ??
    actions.patchFocusBlock ??
    actions.linkFocusBlock ??
    actions.unlinkFocusBlock ??
    actions.deleteFocusBlock ??
    actions.completeCard ??
    (actions.boardFilters ||
    actions.includeUnassigned ||
    actions.workingHours ||
    actions.workingDays ||
    actions.authorizeCalendar ||
    actions.disconnectCalendar ||
    actions.calendarDisplayed
      ? 'planner'
      : null)
  );
}
